// Leads & Sales → AI Sales Brain section: every lead's analysis state, one click into the Brain workspace.
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Search } from "lucide-react";
import { API, STAGE_LABELS } from "./SalesCRM";
import LeadDetail from "./LeadDetail";

const STATUS = { ready: ["Analysed", "bg-lime-100 text-cafe-olive"], generating: ["Analysing…", "bg-amber-100 text-amber-800"],
    error: ["Failed", "bg-red-100 text-red-700"], idle: ["Not analysed", "bg-neutral-100 text-neutral-500"] };

export default function BrainHub() {
    const [data, setData] = useState(null);
    const [cls, setCls] = useState("live");
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState("");
    const [open, setOpen] = useState(null);
    const load = useCallback(async () => {
        try { const { data: d } = await axios.get(`${API}/crm/brain/overview?cls=${cls}`); setData((prev) => (d.cls !== cls ? prev : d)); }
        catch { toast.error("Could not load AI Sales Brain"); }
    }, [cls]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (!data?.counts?.generating) return;
        const t = setInterval(load, 4000); return () => clearInterval(t);
    }, [data?.counts?.generating, load]);

    const leads = (data?.leads || []).filter((l) => (!filter || l.brain_status === filter || (filter === "idle" && !["ready", "generating", "error"].includes(l.brain_status)))
        && (!q || `${l.brand} ${l.location} ${l.business_type}`.toLowerCase().includes(q.toLowerCase())));
    const c = data?.counts || {};

    return (
        <div className="space-y-5" data-testid="brain-hub">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h2 className="font-serif text-2xl text-cafe-espresso">AI Sales Brain</h2>
                    <p className="text-xs text-neutral-500 mt-1 max-w-xl">Paste merchant screenshots, set your direction, and get an evidence-based read on each lead. Works on the same Lead records as the Sales Pipeline — nothing is duplicated.</p>
                </div>
                <button data-testid="open-brain-btn" onClick={() => leads[0] ? setOpen(`${leads[0].id}#brain`) : toast.info("Create a lead in Sales Pipeline first")}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-cafe-espresso text-white text-sm"><Sparkles size={15} /> Open AI Sales Brain</button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[["analysed", "Analysed", "ready"], ["generating", "Analysing now", "generating"], ["not_analysed", "Not analysed yet", "idle"], ["with_materials", "With materials", ""]].map(([k, l, f]) => (
                    <button key={k} data-testid={`brain-count-${k}`} onClick={() => setFilter(filter === f ? "" : f)}
                        className={`text-left bg-white rounded-xl border px-4 py-3 ${filter === f && f ? "border-cafe-olive" : "border-neutral-200"}`}>
                        <div className="font-serif text-2xl text-cafe-espresso">{c[k] ?? "—"}</div>
                        <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 mt-0.5">{l}</div>
                    </button>
                ))}
            </div>
            <div className="flex flex-wrap gap-2 items-center">
                <div className="relative flex-1 min-w-[180px] max-w-xs">
                    <Search size={13} className="absolute left-2.5 top-2.5 text-neutral-400" />
                    <input data-testid="brain-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search leads…" className="w-full pl-8 pr-3 py-1.5 text-sm rounded-full border border-neutral-300 bg-white" />
                </div>
                <select data-testid="brain-class-filter" value={cls} onChange={(e) => setCls(e.target.value)} className="border border-neutral-300 rounded-full px-3 py-1.5 text-xs bg-white">
                    <option value="live">Live customers only</option><option value="all">Include demo/test</option>
                </select>
            </div>
            {!data ? <div className="text-sm text-neutral-500 py-10 text-center">Loading…</div> : !leads.length
                ? <div className="text-sm text-neutral-500 py-10 text-center" data-testid="brain-empty">No leads here yet.</div>
                : <div className="bg-white rounded-xl border border-neutral-200 divide-y" data-testid="brain-list">
                    {leads.map((l) => {
                        const [lbl, cl] = STATUS[l.brain_status] || STATUS.idle;
                        return (
                            <div key={l.id} data-testid={`brain-row-${l.id}`} className="flex flex-wrap items-center gap-2 px-4 py-2.5 text-xs">
                                <button onClick={() => setOpen(`${l.id}#brain`)} className="font-medium text-sm text-cafe-espresso hover:underline text-left min-w-[160px]" data-testid={`brain-open-${l.id}`}>{l.brand}
                                    <span className="block text-[10px] text-neutral-400 font-normal">{[l.business_type, l.location, STAGE_LABELS[l.stage]].filter(Boolean).join(" · ")}</span></button>
                                <span className={`px-2 py-0.5 rounded-full ${cl}`}>{lbl}</span>
                                <span className="text-neutral-500">{l.materials} material(s)</span>
                                {l.has_direction && <span className="text-cafe-terracotta">Direction set</span>}
                                {l.questions > 0 && <span className="text-amber-700">{l.questions} question(s) to ask</span>}
                                {l.has_legacy && <span className="text-neutral-400">legacy import</span>}
                                <span className="flex-1" />
                                <button data-testid={`brain-analyse-${l.id}`} onClick={() => setOpen(`${l.id}#brain`)}
                                    className="flex items-center gap-1 px-3 py-1 rounded-full border border-cafe-espresso text-cafe-espresso"><Sparkles size={11} /> Analyse with AI Sales Brain</button>
                            </div>
                        );
                    })}
                </div>}
            {open && <LeadDetail leadId={open} onClose={() => { setOpen(null); load(); }} />}
        </div>
    );
}
